import { type FormEvent, useState } from "react";
import { useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { useDispute } from "../hooks/useDispute";
import { apiClient, newIdempotencyKey } from "../lib/api-client";
import { queryKeys } from "../lib/queryClient";
import { DisputeStatusBadge } from "../components/StatusBadge";
import { ErrorBanner, LoadingState, describeError } from "../components/States";
import { formatDateTime } from "../lib/format";

export function DisputeDetailPage() {
  const { disputeId } = useParams<{ disputeId: string }>();
  const queryClient = useQueryClient();
  const { data: dispute, isLoading, isError, error, refetch } = useDispute(disputeId);
  const [rebuttal, setRebuttal] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  if (!disputeId) return null;
  if (isLoading) return <LoadingState label="Loading dispute…" />;
  if (isError) return <ErrorBanner error={error} onRetry={() => void refetch()} />;
  if (!dispute) return null;

  async function handleRebuttal(e: FormEvent) {
    e.preventDefault();
    if (rebuttal.trim().length < 1) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await apiClient.submitRebuttal(
        disputeId as string,
        { rebuttal: rebuttal.trim() },
        newIdempotencyKey(),
      );
      await queryClient.invalidateQueries({ queryKey: queryKeys.dispute(disputeId as string) });
      setRebuttal("");
    } catch (err) {
      setSubmitError(describeError(err));
    } finally {
      setIsSubmitting(false);
    }
  }

  return (
    <div className="stack" style={{ maxWidth: 620 }}>
      <div className="row row--between">
        <h1>Dispute</h1>
        <DisputeStatusBadge status={dispute.status} />
      </div>

      <div className="alert alert--info" role="status">
        Resolution is not final until the arbitrator&apos;s multisig transaction confirms onchain.
        This page shows offchain case status only.
      </div>

      <div className="card stack">
        <div>
          <p className="text-sm muted">Category</p>
          <p>{dispute.category}</p>
        </div>
        <div>
          <p className="text-sm muted">Buyer&apos;s claim</p>
          <p className="desc-full">{dispute.claim}</p>
        </div>
        <div>
          <p className="text-sm muted">Seller&apos;s response</p>
          {dispute.sellerRebuttal ? (
            <p className="desc-full">{dispute.sellerRebuttal}</p>
          ) : (
            <p className="muted">No response yet.</p>
          )}
        </div>
        <div>
          <p className="text-sm muted">Opened</p>
          <p>{formatDateTime(dispute.createdAt)}</p>
        </div>
        {dispute.resolvedAt && (
          <div>
            <p className="text-sm muted">Resolved</p>
            <p>{formatDateTime(dispute.resolvedAt)}</p>
          </div>
        )}
      </div>

      {!dispute.sellerRebuttal && (
        <form className="card stack" onSubmit={handleRebuttal}>
          <h2>Respond to this dispute</h2>
          <p className="text-sm muted">
            Only the seller on this order can submit a response. If you&apos;re not the seller, this
            will be rejected by the server.
          </p>
          <div className="field">
            <label htmlFor="rebuttal">Your response</label>
            <textarea
              id="rebuttal"
              maxLength={5_000}
              value={rebuttal}
              onChange={(e) => setRebuttal(e.target.value)}
            />
          </div>
          {submitError && (
            <p className="field-error-text" role="alert">
              {submitError}
            </p>
          )}
          <button type="submit" className="btn btn-primary" disabled={isSubmitting}>
            {isSubmitting ? "Submitting…" : "Submit response"}
          </button>
        </form>
      )}
    </div>
  );
}
